import React, { useEffect, useState } from "react";
import { api, fmtGNF } from "@/lib/api";
import { Boxes, Plus, Save, Trash2, AlertCircle, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";

const LEVELS = ["Maternelle", "Primaire CP1", "Primaire CP2", "Primaire CE1", "Primaire CE2", "Primaire CM1", "Primaire CM2", "Collège 6ème", "Collège 5ème", "Collège 4ème", "Collège 3ème", "Lycée 2nd", "Lycée 1ère", "Lycée Terminale"];

export default function StockPage() {
  const [books, setBooks] = useState([]);
  const [edits, setEdits] = useState({}); // {book_id: new_stock}
  const [loading, setLoading] = useState(true);
  const [newOpen, setNewOpen] = useState(false);
  const [newBook, setNewBook] = useState({ title: "", level: LEVELS[0], price: 0, stock: 0 });

  const load = () => {
    setLoading(true);
    api.get("/books").then(({ data }) => setBooks(data)).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const save = async (id) => {
    const newStock = edits[id];
    if (newStock === undefined || newStock === "") return;
    try {
      await api.patch(`/books/${id}/stock`, { stock: parseInt(newStock) });
      toast.success("Stock mis à jour");
      setEdits(e => { const n = { ...e }; delete n[id]; return n; });
      load();
    } catch { toast.error("Erreur"); }
  };

  const remove = async (id, title) => {
    if (!window.confirm(`Supprimer "${title}" du catalogue ?`)) return;
    try {
      await api.delete(`/books/${id}`);
      toast.success("Manuel supprimé");
      load();
    } catch { toast.error("Erreur"); }
  };

  const create = async () => {
    if (!newBook.title || newBook.price <= 0) { toast.error("Titre et prix requis"); return; }
    try {
      await api.post("/books", { ...newBook, price: parseFloat(newBook.price), stock: parseInt(newBook.stock) });
      toast.success("Manuel ajouté au catalogue");
      setNewOpen(false);
      setNewBook({ title: "", level: LEVELS[0], price: 0, stock: 0 });
      load();
    } catch { toast.error("Erreur"); }
  };

  if (loading) return <div className="text-center py-12"><Loader2 className="h-6 w-6 animate-spin mx-auto text-slate-400" /></div>;

  const lowStockCount = books.filter(b => b.stock < 5).length;

  return (
    <div className="space-y-5" data-testid="page-stock">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <div className="chip mb-2"><Boxes className="h-3 w-3" /><span>Gestion stock</span></div>
          <h2 className="font-heading text-3xl font-extrabold text-[#1E3A8A] tracking-tight">Catalogue & inventaire</h2>
          <p className="text-sm text-slate-500 mt-1">{books.length} référence(s) · {lowStockCount} en alerte stock</p>
        </div>
        <Dialog open={newOpen} onOpenChange={setNewOpen}>
          <DialogTrigger asChild>
            <Button className="bg-[#166534] hover:bg-[#14532d] text-white gap-2" data-testid="add-book-btn"><Plus className="h-4 w-4" /> Ajouter un manuel</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Nouveau manuel</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div>
                <label className="text-xs font-bold uppercase text-slate-500 block mb-1">Titre</label>
                <input value={newBook.title} onChange={e => setNewBook({...newBook, title: e.target.value})} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" data-testid="new-book-title" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-bold uppercase text-slate-500 block mb-1">Niveau</label>
                  <select value={newBook.level} onChange={e => setNewBook({...newBook, level: e.target.value})} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm">
                    {LEVELS.map(l => <option key={l}>{l}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs font-bold uppercase text-slate-500 block mb-1">Prix (GNF)</label>
                  <input type="number" value={newBook.price} onChange={e => setNewBook({...newBook, price: e.target.value})} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" data-testid="new-book-price" />
                </div>
              </div>
              <div>
                <label className="text-xs font-bold uppercase text-slate-500 block mb-1">Stock initial</label>
                <input type="number" value={newBook.stock} onChange={e => setNewBook({...newBook, stock: e.target.value})} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" data-testid="new-book-stock" />
              </div>
              <Button onClick={create} className="w-full bg-[#166534] hover:bg-[#14532d] text-white" data-testid="new-book-save">Enregistrer</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {lowStockCount > 0 && (
        <div className="bg-orange-50 border border-orange-200 rounded-xl p-4 flex items-center gap-3">
          <AlertCircle className="h-5 w-5 text-[#EA580C] flex-shrink-0" />
          <div className="text-sm text-orange-800"><strong>{lowStockCount}</strong> manuel(s) en stock faible (&lt; 5 unités). Pensez à réapprovisionner.</div>
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <table className="w-full">
          <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
            <tr>
              <th className="px-5 py-3 text-left">Titre</th>
              <th className="px-5 py-3 text-left">Niveau</th>
              <th className="px-5 py-3 text-right">Prix</th>
              <th className="px-5 py-3 text-center">Stock actuel</th>
              <th className="px-5 py-3 text-center">Nouveau stock</th>
              <th className="px-5 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {books.map(b => (
              <tr key={b.id} className="hover:bg-slate-50" data-testid={`stock-row-${b.id}`}>
                <td className="px-5 py-3 text-sm font-semibold">{b.title}</td>
                <td className="px-5 py-3 text-sm text-slate-600">{b.level}</td>
                <td className="px-5 py-3 text-sm text-right font-bold text-[#166534]">{fmtGNF(b.price)}</td>
                <td className="px-5 py-3 text-center">
                  <span className={`inline-flex items-center justify-center min-w-[40px] px-2 py-1 rounded-full text-xs font-bold ${b.stock < 5 ? "bg-red-100 text-red-700" : b.stock < 15 ? "bg-orange-100 text-orange-700" : "bg-green-100 text-green-700"}`}>{b.stock}</span>
                </td>
                <td className="px-5 py-3 text-center">
                  <input
                    type="number" min="0"
                    placeholder={String(b.stock)}
                    value={edits[b.id] ?? ""}
                    onChange={e => setEdits(ed => ({...ed, [b.id]: e.target.value}))}
                    className="w-20 text-center border border-slate-300 rounded-md px-2 py-1 text-sm"
                    data-testid={`stock-edit-${b.id}`}
                  />
                </td>
                <td className="px-5 py-3 text-right">
                  <div className="flex justify-end gap-2">
                    <Button size="sm" onClick={() => save(b.id)} disabled={edits[b.id] === undefined} className="bg-[#1E3A8A] text-white text-xs gap-1 disabled:opacity-50" data-testid={`stock-save-${b.id}`}>
                      <Save className="h-3 w-3" /> Sauver
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => remove(b.id, b.title)} className="border-red-300 text-red-700 hover:bg-red-50 text-xs" data-testid={`stock-delete-${b.id}`}>
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
